import {
  ProjectModel,
  useCompleteProject,
  useRevertCompleteProject,
} from 'api-hooks/projects';
import { CheckCircleSVG, CloseSVG } from 'common/assets';
import { AuthorizationRules } from 'common/constants';
import { ProjectStatusEnum } from 'common/constants/enum';
import stylesColors from 'common/styles/styles.colors';
import DeleteButton from 'components/common/delete-button';
import notification from 'components/common/notification';
import Button from 'components/elements/buttons/button';
import { PopoverMenuType } from 'components/popover';
import { useAuthorization } from 'hooks/use-authorization';
import useDialog from 'hooks/use-dialog';
import useDismiss from 'hooks/use-dismiss';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

export default function useProjectCompleteAction(project: ProjectModel) {
  const onDismiss = useDismiss('projects');
  const { t } = useTranslation();
  const dialog = useDialog();
  const completeMutate = useCompleteProject();
  const revertCompleteMutate = useRevertCompleteProject();
  const { can } = useAuthorization();
  const status = project?.status;
  const isCompleted = status === ProjectStatusEnum.Completed;

  const isAllowed = React.useMemo(() => {
    switch (status) {
      case ProjectStatusEnum.Active:
      case ProjectStatusEnum.Completed:
        return true;
      default:
        return false;
    }
  }, [status]);

  const completePermission =
    can(AuthorizationRules.ProjectsComplete) && isAllowed && !isCompleted;

  const revertCompletePermission =
    can(AuthorizationRules.ProjectsRevertComplete) && isAllowed && isCompleted;

  const onRevertComplete = React.useCallback(
    (currentId?: string) => {
      const _id = currentId ?? project.id;
      const message = {
        id: _id || 'undefined',
        message: 'Invalid Revert Complete Action (FE)',
      };

      if (!_id) {
        notification.error({
          message: JSON.stringify(message),
        });
        return;
      }

      dialog.showConfirmation({
        message: t(
          'common:do_you_want_to_change_this_project_status_to_incomplete',
        ),
        title: t('common:confirmation'),
        onPositiveAction: async (onClose) => {
          try {
            const result = await revertCompleteMutate.mutateAsync({
              projectId: _id,
            });
            notification.success({ message: result?.message });
            onDismiss(_id);
            onClose();
          } catch (error) {
            notification.error({ message: error.message });
          }
        },
      });
    },
    [project.id, dialog, t, revertCompleteMutate, onDismiss],
  );

  const onComplete = React.useCallback(
    async (currentId?: string) => {
      const _id = currentId ?? project.id;
      const message = {
        id: _id || 'undefined',
        message: 'Invalid Complete Action (FE)',
      };

      if (!_id) {
        notification.error({
          message: JSON.stringify(message),
        });
        return;
      }

      dialog.showConfirmation({
        message: t(
          'common:do_you_want_to_change_this_project_status_to_complete',
        ),
        title: t('common:confirmation'),
        onPositiveAction: async (onClose) => {
          try {
            const result = await completeMutate.mutateAsync({
              projectId: _id,
            });
            notification.success({ message: result?.message });
            onDismiss(_id);
            onClose();
          } catch (error) {
            notification.error({ message: error.message });
          }
        },
        onNegativeAction: (onClose) => {
          onClose();
        },
      });
    },
    [completeMutate, dialog, onDismiss, project.id, t],
  );

  const completeAction: PopoverMenuType = {
    label: t('common:complete'),
    onClick: onComplete,
    isAllowed: completePermission,
    isError: false,
    key: `complete-${project.id}`,
  };

  const revertCompleteAction: PopoverMenuType = {
    label: t('common:incomplete'),
    onClick: onRevertComplete,
    isAllowed: revertCompletePermission,
    isError: true,
    key: `incomplete-${project.id}`,
  };

  const completeComponent = completePermission && (
    <Button
      startEnhancer={(color, size) => (
        <CheckCircleSVG color={color} size={size} />
      )}
      onClick={() => {
        onComplete();
      }}
      isLoading={completeMutate.isLoading}
    >
      {t('common:complete')}
    </Button>
  );

  const revertCompleteComponent = revertCompletePermission && (
    <DeleteButton
      text={t('common:incomplete')}
      onClick={() => {
        onRevertComplete();
      }}
      icon={<CloseSVG color={stylesColors.redDefault} width={16} height={16} />}
      isLoading={revertCompleteMutate.isLoading}
    />
  );

  return {
    revertCompleteMutate,
    revertCompletePermission,
    onRevertComplete,
    revertCompleteAction,
    revertCompleteComponent,
    completeMutate,
    completePermission,
    onComplete,
    completeAction,
    completeComponent,
  };
}
